import { useState, useEffect } from 'react';

const NAV_ITEMS = [
  { label: '브랜드 소개', href: '#brand' },
  { label: '갤러리', href: '#gallery' },
  { label: '반영구 문신', href: '#semi-permanent' },
  { label: '안티에이징', href: '#anti-aging' },
  { label: '다이어트 & 한방', href: '#diet-herbal' },
  { label: '가격표', href: '#pricing' },
  { label: '고객 후기', href: '#reviews' },
  { label: '예약 & 오시는 길', href: '#reservation' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gold/20' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); handleNav('#hero'); }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <img
              src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/f698c07f-0cab-4570-b763-0881800c24d1_unnamed.png?v=279980a50a992796d79c482ddf8958e7"
              alt="헤라브로우크리닉 로고"
              className="h-14 w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNav(item.href)}
                className={`text-sm font-sans font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                  scrolled
                    ? 'text-gray-700 hover:text-gold-dark'
                    : 'text-white/90 hover:text-gold-shine'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            <a
              href="tel:01067404061"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                scrolled
                  ? 'bg-plum text-white hover:bg-plum-dark'
                  : 'bg-white/15 text-white border border-white/40 hover:bg-white/25'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm"></i></span>
              전화 상담
            </a>
            <a
              href="https://open.kakao.com/o/sFFmWDmi"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                scrolled
                  ? 'bg-[#FAE100] text-[#3A1D1D] hover:bg-yellow-400'
                  : 'bg-[#FAE100]/90 text-[#3A1D1D] hover:bg-[#FAE100]'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-kakao-talk-fill text-sm"></i></span>
              카카오톡
            </a>
            <a
              href="https://line.me/ti/p/5UmAcjQ_dg"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                scrolled
                  ? 'bg-[#06C755] text-white hover:bg-green-600'
                  : 'bg-[#06C755]/90 text-white hover:bg-[#06C755]'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-line-fill text-sm"></i></span>
              LINE
            </a>
            <a
              href="https://www.instagram.com/hera__brow"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                scrolled
                  ? 'bg-gradient-to-r from-[#f09433] via-[#e6683c] to-[#dc2743] text-white hover:opacity-90'
                  : 'bg-white/15 text-white border border-white/40 hover:bg-white/25'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-instagram-fill text-sm"></i></span>
              Instagram
            </a>
            <a
              href="https://business.google.com/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                scrolled
                  ? 'bg-white text-gray-700 border border-gray-200 hover:border-gray-400'
                  : 'bg-white/15 text-white border border-white/40 hover:bg-white/25'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-google-fill text-sm"></i></span>
              Google
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`lg:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${scrolled ? 'text-gray-700' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gold/20 py-4 px-6">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNav(item.href)}
                className="block w-full text-left py-3 text-sm text-gray-700 font-medium border-b border-gray-100 last:border-0 hover:text-gold-dark cursor-pointer whitespace-nowrap"
              >
                {item.label}
              </button>
            ))}
            <div className="flex gap-2 mt-4">
              <a href="tel:01067404061" className="flex-1 flex items-center justify-center gap-1 bg-plum text-white py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill"></i></span>전화
              </a>
              <a href="https://open.kakao.com/o/sFFmWDmi" target="_blank" rel="nofollow noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-[#FAE100] text-[#3A1D1D] py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-kakao-talk-fill"></i></span>카카오
              </a>
              <a href="https://line.me/ti/p/5UmAcjQ_dg" target="_blank" rel="nofollow noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-[#06C755] text-white py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-line-fill"></i></span>LINE
              </a>
              <a href="https://www.instagram.com/hera__brow" target="_blank" rel="nofollow noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-gradient-to-r from-[#f09433] via-[#e6683c] to-[#dc2743] text-white py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-instagram-fill"></i></span>인스타
              </a>
              <a href="https://business.google.com/" target="_blank" rel="nofollow noopener noreferrer" className="flex-1 flex items-center justify-center gap-1 bg-white text-gray-700 border border-gray-200 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-google-fill"></i></span>Google
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Floating Contact Buttons */}
      <div className="fixed bottom-8 right-6 z-50 flex flex-col gap-3">
        <a
          href="tel:01067404061"
          className="w-12 h-12 flex items-center justify-center bg-plum text-white rounded-full cursor-pointer hover:bg-plum-dark transition-colors duration-200"
          title="전화 상담"
        >
          <i className="ri-phone-fill text-lg"></i>
        </a>
        <a
          href="https://open.kakao.com/o/sFFmWDmi"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="w-12 h-12 flex items-center justify-center bg-[#FAE100] text-[#3A1D1D] rounded-full cursor-pointer hover:bg-yellow-400 transition-colors duration-200"
          title="카카오톡 상담"
        >
          <i className="ri-kakao-talk-fill text-lg"></i>
        </a>
        <a
          href="https://line.me/ti/p/5UmAcjQ_dg"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="w-12 h-12 flex items-center justify-center bg-[#06C755] text-white rounded-full cursor-pointer hover:bg-green-600 transition-colors duration-200"
          title="LINE 상담"
        >
          <i className="ri-line-fill text-lg"></i>
        </a>
        <a
          href="https://www.instagram.com/hera__brow"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#dc2743] text-white rounded-full cursor-pointer hover:opacity-90 transition-opacity duration-200"
          title="Instagram"
        >
          <i className="ri-instagram-fill text-lg"></i>
        </a>
        <a
          href="https://business.google.com/"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="w-12 h-12 flex items-center justify-center bg-white text-gray-700 rounded-full cursor-pointer hover:bg-gray-100 transition-colors duration-200"
          title="Google 비즈니스"
        >
          <i className="ri-google-fill text-lg"></i>
        </a>
      </div>
    </>
  );
}
